import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQrcode, faWallet, faGamepad, faFireAlt, faGift, faCoins,
  faArrowRight, faChevronDown, faChevronUp, faCopy, faCheck, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { generateAddress, getAddresses } from '../../services/walletService';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode.react';
import styles from './Receive.module.css';

const Receive = () => {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [txDetails, setTxDetails] = useState([]);
  const [hasShownFeeInfo, setHasShownFeeInfo] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const result = await getAddresses();
        const list = result?.data?.addresses || [];
        setAddresses(list);

        if (list.length > 0) setSelectedAddress(list[0].address);

        if (list.length >= 1 && !hasShownFeeInfo) {
          setHasShownFeeInfo(true);
          toast.info(t('fee_info'));
        }
      } catch (error) {
        toast.error(t('error_fetching_addresses'));
      }
    };

    fetchAddresses();
  }, [hasShownFeeInfo, t]);

  const handleGenerateAddress = async () => {
    try {
      const result = await generateAddress();
      const addr = result?.data;
      if (!addr?.address) throw new Error('No address returned');
      setAddresses((prev) => [...prev, addr]);
      setSelectedAddress(addr.address);
      setTxDetails(addr.txDetails || []);
      toast.success(t('new_address_generated'));
    } catch (error) {
      toast.error(`${t('error_generating_address')}: ${error.response?.data?.error || t('error_generating_address')}`);
    }
  };

  const handleAddressSelect = (address) => setSelectedAddress(address);

  const isOutgoingAddress = (address) =>
    (txDetails || []).some((tx) => (tx.outputs || []).some((o) => o.address === address));

  const toggleShowAllAddresses = () => setShowAllAddresses((v) => !v);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(selectedAddress);
      toast.success(t('copied_to_clipboard'));
    } catch {
      toast.error(t('copy_failed'));
    }
  };

  // primeros outputs si existen
  const tx0 = txDetails?.[0];
  const gameFund = tx0?.outputs?.find((o) => o.type === 'GAME_FUND_ADDRESS');
  const burnAddr  = tx0?.outputs?.find((o) => o.type === 'BURN_ADDRESS');
  const rewards   = tx0?.outputs?.find((o) => o.type === 'REWARDS_ADDRESS');

  const linkTx = (txid) => `https://explorer.menelik.cc/txid/${txid}`;
  const linkAddress = (addr) => `https://explorer.menelik.cc/address/${addr}`;

  return (
    <div className={styles.root}>
      <ToastContainer />

      <header className={styles.header}>
        <h1 className={styles.title}>
          <FontAwesomeIcon icon={faWallet} /> {t('receive_menelik')}
        </h1>
        <p className={styles.subtitle}>{t('receive_subtitle')}</p>
      </header>

      <div className={styles.grid}>
        {/* Columna izquierda: QR y dirección seleccionada */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <FontAwesomeIcon icon={faWallet} />
            <span>{t('receive_menelik')}</span>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.actionsRow}>
              <button className={styles.btnPrimary} onClick={handleGenerateAddress}>
                {t('generate_new_address')}
              </button>

              {selectedAddress && (
                <button className={styles.btnGhost} onClick={handleCopy} title={t('copy_address')}>
                  <FontAwesomeIcon icon={faCopy} /> {t('copy')}
                </button>
              )}
            </div>

            {selectedAddress ? (
              <div className={styles.qrWrap}>
                <p className={styles.addressText} title={selectedAddress}>
                  {selectedAddress}
                </p>
                <div className={styles.qrCard}>
                  <QRCode value={selectedAddress} size={220} />
                </div>
              </div>
            ) : (
              <p className={styles.muted}>{t('no_address_selected')}</p>
            )}
          </div>
        </section>

        {/* Columna derecha: lista de direcciones */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <FontAwesomeIcon icon={faWallet} />
            <span>{t('your_addresses')}</span>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.addressList}>
              {(addresses || [])
                .slice(0, showAllAddresses ? addresses.length : 9)
                .map((addr, i) => {
                  const active = selectedAddress === addr.address;
                  const outgoing = isOutgoingAddress(addr.address);
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`${styles.addressItem} ${active ? styles.active : ''}`}
                      onClick={() => handleAddressSelect(addr.address)}
                      title={addr.address}
                    >
                      <span className={styles.addressDot} />
                      <span className={styles.addressLabel}>{addr.address}</span>
                      {outgoing && (
                        <span className={styles.badgeDanger}>
                          {t('outgoing')} <FontAwesomeIcon icon={faArrowRight} />
                        </span>
                      )}
                      {active && (
                        <span className={styles.badgeOk}>
                          <FontAwesomeIcon icon={faCheck} /> {t('selected')}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>

            {addresses.length > 9 && (
              <button className={styles.btnLink} onClick={toggleShowAllAddresses}>
                {showAllAddresses ? (
                  <>
                    {t('see_less')} <FontAwesomeIcon icon={faChevronUp} />
                  </>
                ) : (
                  <>
                    {t('see_more')} <FontAwesomeIcon icon={faChevronDown} />
                  </>
                )}
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Detalles de la Transacción */}
      {txDetails.length > 0 && (
        <section className={styles.txSection}>
          <div className={`${styles.card} ${styles.cardAccent}`}>
            <div className={styles.cardBody}>
              <div className={styles.txHeader}>
                <div className={styles.txIconTitle}>
                  <FontAwesomeIcon icon={faQrcode} className={`${styles.txIcon} ${styles.icoTx}`} />
                  <h3 className={styles.txTitle}>{t('transaction_details')}</h3>
                </div>

                <div className={styles.txidRow}>
                  <span className={styles.txidLabel}>TXID</span>
                  {tx0?.txid ? (
                    <a
                      className={`${styles.txidValue} ${styles.link}`}
                      href={linkTx(tx0.txid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={tx0.txid}
                    >
                      {tx0.txid}
                      <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.linkArrow} />
                    </a>
                  ) : (
                    <span className={styles.txidValue}>—</span>
                  )}
                </div>
              </div>

              <div className={styles.txGrid}>
                {/* Game fund */}
                <div className={`${styles.txCard} ${styles.txGame}`}>
                  <div className={styles.txCardHead}>
                    <FontAwesomeIcon icon={faGamepad} className={styles.icoGame} />
                    {t('game_fund')}
                  </div>
                  <div className={styles.txCardBody}>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faCoins} />
                      <span>{t('game_fund_distribution')}</span>
                    </div>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faArrowRight} />
                      {gameFund?.address ? (
                        <a
                          className={`${styles.addrMono} ${styles.link}`}
                          href={linkAddress(gameFund.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={gameFund.address}
                        >
                          {gameFund.address}
                          <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.linkArrow} />
                        </a>
                      ) : (
                        <span className={styles.addrMono}>—</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Burn address */}
                <div className={`${styles.txCard} ${styles.txBurn}`}>
                  <div className={styles.txCardHead}>
                    <FontAwesomeIcon icon={faFireAlt} className={styles.icoBurn} />
                    {t('burn_address')}
                  </div>
                  <div className={styles.txCardBody}>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faCoins} />
                      <span>{t('burned_tokens')}</span>
                    </div>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faArrowRight} />
                      {burnAddr?.address ? (
                        <a
                          className={`${styles.addrMono} ${styles.link}`}
                          href={linkAddress(burnAddr.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={burnAddr.address}
                        >
                          {burnAddr.address}
                          <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.linkArrow} />
                        </a>
                      ) : (
                        <span className={styles.addrMono}>—</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rewards / Airdrops */}
                <div className={`${styles.txCard} ${styles.txReward}`}>
                  <div className={styles.txCardHead}>
                    <FontAwesomeIcon icon={faGift} className={styles.icoReward} />
                    {t('rewards_airdrops')}
                  </div>
                  <div className={styles.txCardBody}>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faCoins} />
                      <span>{t('rewards_distribution')}</span>
                    </div>
                    <div className={styles.txRow}>
                      <FontAwesomeIcon icon={faArrowRight} />
                      {rewards?.address ? (
                        <a
                          className={`${styles.addrMono} ${styles.link}`}
                          href={linkAddress(rewards.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={rewards.address}
                        >
                          {rewards.address}
                          <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.linkArrow} />
                        </a>
                      ) : (
                        <span className={styles.addrMono}>—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>{/* /txGrid */}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Receive;
